import { useCallback, useEffect, useState } from 'react'
import { ReactState } from '../../React/Common/Contexts'

export type usePaginationReturn<T> = [number, (num: number) => T[], number, (num: number) => void]

type usePaginationHook = <T>(
    data: T[],
    isPaginationEnabled: boolean,
    pageNumber: number,
    initialRowsPerPage: number,
    isLocalPagination: boolean
) => usePaginationReturn<T>

export const usePagination: usePaginationHook = <T extends unknown>(
    data: T[],
    isPaginationEnabled: boolean,
    pageNumber: number,
    initialRowsPerPage: number,
    isLocalPagination: boolean
): usePaginationReturn<T> => {
    const [pages, setPages]: ReactState<T[][]> = useState<T[][]>([[]])
    const [currentPageNumber, setCurrentPageNumber]: ReactState<number> = useState<number>(pageNumber)
    const [rowsPerPage, setRowsPerPage]: ReactState<number> = useState<number>(initialRowsPerPage)

    useEffect((): void => {
        setCurrentPageNumber(pageNumber)
    }, [pageNumber])

    useEffect((): void => {
        if (!isLocalPagination || !isPaginationEnabled) {
            setPages([data])
            return
        }

        const newPages: T[][] = data.reduce((acc: T[][], record: T, idx: number): T[][] => {
            const pageIdx: number = Math.floor(idx / rowsPerPage)
            acc[pageIdx] = ([] as T[]).concat(acc[pageIdx] || [], record)
            return acc
        }, [] as T[][])

        if (!newPages.length) {
            newPages.push([])
        }

        setPages(newPages)
    }, [data, rowsPerPage, isPaginationEnabled, isLocalPagination])

    const getPage: (num: number) => T[] = useCallback(
        (num: number): T[] => {
            if (num !== currentPageNumber) {
                setCurrentPageNumber(num)
            }

            if (!isLocalPagination || !isPaginationEnabled) {
                return pages[0]
            }

            return pages[num]
        },
        [currentPageNumber, isLocalPagination, isPaginationEnabled, pages]
    )

    const changeRowsPerPage: (num: number) => void = useCallback((num: number): void => setRowsPerPage(num), [])

    return [currentPageNumber, getPage, rowsPerPage, changeRowsPerPage]
}
